/**
 * The roster grid's per-employee assignment drawer — Task T2-E-web.
 *
 * Reuses the drawer pattern `attendance/DayDrawer.tsx` established, exactly as
 * `people/PeopleDrawer.tsx` does: `role="dialog"` with deliberately **no
 * `aria-modal`** (nothing outside it is inert, and there is no focus trap), focus
 * lands on the drawer's own heading on open, and `Esc` closes it.
 *
 * Only ever mounted when the session holds `workforce.manage_shifts`
 * (`RosterScreen.tsx` gates the cell click that opens it) — the honesty gate
 * `nav/menu.ts`'s own boxed comment states for the whole app: the server refuses the
 * two writes below on its own regardless of what this screen shows.
 *
 * ── The assignment list is not "this employee's own", entirely ──────────────
 *
 * `GET /shift-assignments?employee_id=…&include_ended=true` narrows only the
 * `scope_type: employee` rows to this one employee; the four non-employee-scoped
 * rows (`job_title`/`department`/`branch`/`company`) are company-wide policy and
 * stay in the response unfiltered (`api/roster.ts`'s own `shiftAssignmentsPath`
 * docblock, transcribed from `src/modules/attendance/routes.ts`). `roster.drawer.
 * list.note` says so on screen, once, rather than letting every row in the table
 * imply it is this employee's exclusively.
 *
 * ── Warnings never block; two different overlaps both do ─────────────────────
 *
 * A `201` from "Gán ca" is a success even when it carries `warnings` — they render as
 * chips under `roster.assign.done`'s sentence, never as a reason the row did not
 * save. `400 WORKFORCE_SHIFT_OVERLAP` and `409 WORKFORCE_SHIFT_ASSIGNMENT_OVERLAP`
 * are the two shapes that DO block, and `ApiErrorNote` renders either's own accurate,
 * user-safe sentence (`src/lib/errors.ts`) with no client-side re-wording needed.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';

import { useI18n, type Translate } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import {
  SHIFT_ASSIGNMENTS_PATH,
  shiftAssignmentPath,
  shiftAssignmentsPath,
  type Shift,
  type ShiftAssignment,
  type ShiftAssignmentCreateRequest,
  type ShiftAssignmentCreateResponse,
  type ShiftAssignmentEndRequest,
  type ShiftAssignmentList,
} from '../../api/roster';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import {
  SCOPE_TYPE_LABEL_KEY,
  assignmentSkipReasonKey,
  buildAssignRequest,
  shiftLabel,
} from '../../roster/roster';
import { DateField } from '../../components/DateField';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { EndAssignmentPanel } from './EndAssignmentPanel';
import { WarningChipList } from './WarningChips';

export interface RosterDrawerProps {
  readonly employeeId: string;
  readonly employeeName: string;
  /** The full catalogue this screen fetched — active and inactive, for the lookup this file's own header explains. */
  readonly shifts: readonly Shift[];
  readonly timezone: string;
  readonly onClose: () => void;
  /** Bumps the grid's own reload token — an assign or an end changes what it resolves. */
  readonly onChanged: () => void;
}

function scopeLabel(t: Translate, row: ShiftAssignment): string {
  const label = t(SCOPE_TYPE_LABEL_KEY[row.scope_type]);
  return row.scope_type === 'employee' ? label : `${label}: ${row.scope_key}`;
}

export function RosterDrawer({
  employeeId,
  employeeName,
  shifts,
  timezone,
  onClose,
  onChanged,
}: RosterDrawerProps): ReactNode {
  const { t, locale } = useI18n();
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  const [reloadToken, setReloadToken] = useState(0);
  const list = useApiQuery<ShiftAssignmentList>(
    shiftAssignmentsPath({ employeeId, includeEnded: true }),
    reloadToken,
  );

  const shiftById = useMemo(() => new Map(shifts.map((shift) => [shift.id, shift])), [shifts]);
  const activeShifts = useMemo(() => shifts.filter((shift) => shift.active !== false), [shifts]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [employeeId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  /* ── "Gán ca" ──────────────────────────────────────────────────────────── */
  const [shiftId, setShiftId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const assign = useApiMutation<ShiftAssignmentCreateRequest, ShiftAssignmentCreateResponse>(
    SHIFT_ASSIGNMENTS_PATH,
    'POST',
  );
  const draftBody = buildAssignRequest({ employeeId, shiftId, effectiveFrom, effectiveTo });

  // `submit` resolves rather than rejects (`useApiMutation`'s own docblock), so a
  // `.then()` on it fires on a refused create exactly as it fires on a success — a
  // spurious full reload on every rejected "Gán ca". The same bug `confirmEnd` below
  // had, fixed the same way: this effect only fires on the transition **to** `'ok'`.
  useEffect(() => {
    if (assign.status === 'ok') {
      setReloadToken((value) => value + 1);
      onChanged();
    }
  }, [assign.status]);

  const submitAssign = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (draftBody === null || assign.status === 'pending') return;
    void assign.submit(draftBody);
  };

  const created = assign.status === 'ok' ? (assign.data?.created[0] ?? null) : null;
  const skipped = assign.status === 'ok' ? (assign.data?.skipped[0] ?? null) : null;

  /* ── "Kết thúc" ────────────────────────────────────────────────────────── */
  const [endTarget, setEndTarget] = useState<ShiftAssignment | null>(null);
  const end = useApiMutation<ShiftAssignmentEndRequest, ShiftAssignment>(
    SHIFT_ASSIGNMENTS_PATH,
    'DELETE',
  );

  const openEnd = (row: ShiftAssignment): void => {
    end.reset();
    setEndTarget(row);
  };

  // `submit` resolves rather than rejects (`useApiMutation`'s own docblock), so a
  // `.then()` on it runs on a refusal exactly as it runs on a success — closing the
  // panel there would hide the very error `EndAssignmentPanel` exists to show. This
  // effect fires only on the transition **to** `'ok'`, which a refusal never reaches;
  // `onChanged` is deliberately not in the dependency array, the same reason
  // `PeopleScreen.tsx`'s own debounce effect gives for `setParam` — it must fire once
  // per settled status change, not once per render its own call happens to cause.
  useEffect(() => {
    if (end.status === 'ok') {
      setEndTarget(null);
      setReloadToken((value) => value + 1);
      onChanged();
    }
  }, [end.status]);

  const confirmEnd = (effectiveToDraft: string): void => {
    if (endTarget === null || end.status === 'pending') return;
    const body: ShiftAssignmentEndRequest =
      effectiveToDraft === '' ? {} : { effective_to: effectiveToDraft };
    void end.submit(body, shiftAssignmentPath(endTarget.id));
  };

  const rows = list.data?.assignments ?? [];

  return (
    <aside
      className="wp-drawer"
      role="dialog"
      aria-labelledby="t2ew-drawer-title"
      data-testid="t2ew-drawer"
    >
      <div className="wp-screen-head">
        <h2 className="wp-section__title" id="t2ew-drawer-title" tabIndex={-1} ref={headingRef}>
          {t('roster.drawer.title', { name: employeeName })}
        </h2>
        <button
          type="button"
          className="wp-button"
          onClick={onClose}
          data-testid="t2ew-drawer-close"
        >
          {t('roster.drawer.close')}
        </button>
      </div>

      <section aria-labelledby="t2ew-assign-heading" className="wp-stack">
        <h3 className="wp-section__title" id="t2ew-assign-heading">
          {t('roster.assign.heading')}
        </h3>

        {activeShifts.length === 0 ? (
          <p className="wp-small wp-muted" data-testid="t2ew-assign-no-shifts">
            {t('roster.assign.noShifts')}
          </p>
        ) : (
          <form className="wp-stack" onSubmit={submitAssign} data-testid="t2ew-assign-form">
            <div className="wp-field">
              <label className="wp-field__label" htmlFor="t2ew-assign-shift">
                {t('roster.assign.shift')}
              </label>
              <select
                id="t2ew-assign-shift"
                className="wp-field__input"
                value={shiftId}
                onChange={(event) => {
                  setShiftId(event.target.value);
                }}
                data-testid="t2ew-assign-shift"
              >
                <option value="">{t('roster.assign.shift.placeholder')}</option>
                {activeShifts.map((shift) => (
                  <option key={shift.id} value={shift.id}>
                    {shiftLabel(shift, shift.id)}
                  </option>
                ))}
              </select>
            </div>

            <DateField
              id="t2ew-assign-from"
              testId="t2ew-assign-from"
              label={t('roster.assign.effectiveFrom')}
              value={effectiveFrom}
              onChange={setEffectiveFrom}
            />

            <DateField
              id="t2ew-assign-to"
              testId="t2ew-assign-to"
              label={t('roster.assign.effectiveTo')}
              value={effectiveTo}
              onChange={setEffectiveTo}
            />

            <div className="wp-filters__actions">
              <button
                type="submit"
                className="wp-button wp-button--primary"
                disabled={draftBody === null || assign.status === 'pending'}
                data-testid="t2ew-assign-submit"
              >
                {assign.status === 'pending'
                  ? t('roster.assign.submitting')
                  : t('roster.assign.submit')}
              </button>
            </div>
          </form>
        )}

        {assign.status === 'error' && assign.error !== null ? (
          <ApiErrorNote
            error={assign.error}
            subject={t('roster.drawer.subject')}
            testId="t2ew-assign-error"
          />
        ) : null}

        {created !== null ? (
          <StatusNote severity="low" title={t('roster.assign.done')} testId="t2ew-assign-done">
            <WarningChipList warnings={created.warnings} testPrefix="t2ew-assign" />
          </StatusNote>
        ) : skipped !== null ? (
          <StatusNote
            severity="medium"
            title={t(assignmentSkipReasonKey(skipped.reason))}
            testId="t2ew-assign-skipped"
          />
        ) : null}
      </section>

      <section aria-labelledby="t2ew-list-heading" className="wp-stack">
        <h3 className="wp-section__title" id="t2ew-list-heading">
          {t('roster.drawer.list.heading')}
        </h3>

        {list.status === 'loading' ? (
          <Skeleton
            rows={3}
            variant="block"
            label={t('roster.drawer.loading')}
            testId="t2ew-list-skeleton"
          />
        ) : null}

        {list.status === 'error' && list.error !== null ? (
          <ApiErrorNote
            error={list.error}
            onRetry={list.reload}
            subject={t('roster.drawer.subject')}
            testId="t2ew-list-error"
          />
        ) : null}

        {list.status === 'ok' && rows.length === 0 ? (
          <EmptyState title={t('roster.drawer.empty')} testId="t2ew-list-empty" />
        ) : null}

        {list.status === 'ok' && rows.length > 0 ? (
          <>
            <div className="wp-table-scroll">
              <table className="wp-table" data-testid="t2ew-list">
                <thead>
                  <tr>
                    <th scope="col">{t('roster.drawer.list.col.scope')}</th>
                    <th scope="col">{t('roster.drawer.list.col.shift')}</th>
                    <th scope="col">{t('roster.drawer.list.col.effective')}</th>
                    <th scope="col">{t('roster.drawer.list.col.warnings')}</th>
                    <th scope="col">{t('roster.drawer.list.col.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const ended = row.ended_at !== null && row.ended_at !== undefined;
                    return (
                      <tr key={row.id} data-testid={`t2ew-list-row-${row.id}`}>
                        <td data-testid={`t2ew-list-scope-${row.id}`}>{scopeLabel(t, row)}</td>
                        <td data-testid={`t2ew-list-shift-${row.id}`}>
                          {shiftLabel(shiftById.get(row.shift_id), row.shift_id)}
                        </td>
                        <td data-testid={`t2ew-list-effective-${row.id}`}>
                          {ended
                            ? t('roster.drawer.list.ended', {
                                date: formatInstant(row.ended_at, locale, timezone),
                              })
                            : row.effective_to === null || row.effective_to === undefined
                              ? t('roster.drawer.list.effective.openEnded', {
                                  from: row.effective_from,
                                })
                              : t('roster.drawer.list.effective.ranged', {
                                  from: row.effective_from,
                                  to: row.effective_to,
                                })}
                        </td>
                        <td data-testid={`t2ew-list-warnings-${row.id}`}>
                          {row.warnings.length === 0 ? (
                            '—'
                          ) : (
                            <WarningChipList
                              warnings={row.warnings}
                              testPrefix={`t2ew-list-${row.id}`}
                            />
                          )}
                        </td>
                        <td>
                          {ended ? null : (
                            <button
                              type="button"
                              className="wp-button"
                              onClick={() => {
                                openEnd(row);
                              }}
                              data-testid={`t2ew-end-open-${row.id}`}
                            >
                              {t('roster.end.action')}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="wp-small wp-muted" data-testid="t2ew-list-note">
              {t('roster.drawer.list.note')}
            </p>
          </>
        ) : null}

        {endTarget === null ? null : (
          <EndAssignmentPanel
            shiftLabel={shiftLabel(shiftById.get(endTarget.shift_id), endTarget.shift_id)}
            scopeLabel={scopeLabel(t, endTarget)}
            pending={end.status === 'pending'}
            error={end.status === 'error' ? end.error : null}
            onConfirm={confirmEnd}
            onCancel={() => {
              setEndTarget(null);
              end.reset();
            }}
          />
        )}
      </section>
    </aside>
  );
}
